/* eslint-disable jsx-a11y/no-static-element-interactions */

import React, { cloneElement, useMemo, useState, useRef, useEffect } from 'react';
import * as _ from 'lodash';
import classNameBind from 'classnames/bind';
import {
  offset,
  flip,
  shift,
  size,
  autoUpdate,
  arrow,
  useFloating,
  useInteractions,
  useRole,
  useHover,
  useDismiss,
  useId,
  useClick,
  FloatingFocusManager,
  FloatingTree,
  FloatingNode,
  useFloatingNodeId,
  useFloatingParentNodeId,
  safePolygon,
  FloatingPortal,
  useListNavigation,
} from '@floating-ui/react-dom-interactions';
import PopoverSection from '../PopoverSection';
import styles from '../../Popover.module.css';
import positionOptions from '../../../../utils/methods/positionOptions';
import { ResizeAware } from '../../../ResizeAware';

const bindClasses = classNameBind.bind(styles);
const propEnums = {
  position: positionOptions,
};
const PopoverWrapping = (props) => {
  const { children, render, position, onOpen, onClose,
    hoverTrigger, disabled, fixedWidth, minWidth,
    customMaxWidth, hoverEnable, submenu,
    noPadding, speechBubble, subdued, insideModal,
    dataId, clickTrigger, onKeyDown, onBlur, focusOrder, onToggle, padding } = props;
  const [open, setOpen] = useState(null);
  const nodeId = useFloatingNodeId();
  const parentId = useFloatingParentNodeId();
  const arrowRef = useRef(null);
  useEffect(() => {
    if (open !== null) {
      if (open) {
        onOpen();
      } else {
        onClose();
        onBlur({}, onToggle, open);
      }
    }
  }, [open]);

  const { x, y, reference, floating, strategy, context, placement, middlewareData, update } = useFloating({
    open,
    onOpenChange: setOpen,
    nodeId: nodeId || null,
    middleware: [
      offset(speechBubble ? 8 : 0),
      flip(),
      shift(),
      ...(speechBubble ? [arrow({ element: arrowRef })] : []),
      size({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            width: fixedWidth && !customMaxWidth
              ? `${rects.reference.width}px`
              : 'auto',
            minWidth: minWidth
              ? `${rects.reference.width}px`
              : 'auto',
            maxWidth: customMaxWidth ? `${customMaxWidth}px` : 'unset',
          });
        },
      }),
    ],
    placement: position,
    whileElementsMounted: autoUpdate,
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useHover(context, {
      enabled: (hoverTrigger || hoverEnable) && !disabled,
      handleClose: safePolygon({
        restMs: 25,
        blockPointerEvents: false,
      }),
    }),
    useClick(context, {
      enabled: (!hoverTrigger || clickTrigger || hoverEnable) && !disabled,
    }),
    useListNavigation(context),
    useRole(context),
    useDismiss(context),

  ]);

  // pasted functionality from mergeRefs package
  // eslint-disable-next-line no-unused-expressions, no-param-reassign
  const mergeRefs = f => (r) => { f.forEach((n) => { typeof n === 'function' ? n(r) : n != null && (n.current = r); }); };

  // Preserve the consumer's ref
  const ref = useMemo(() => mergeRefs([reference, children.ref]), [
    reference,
    children,
  ]);

  const id = useId();
  const labelId = `${id}-label`;
  const descriptionId = `${id}-description`;
  const staticSide = {
    top: 'bottom',
    right: 'left',
    bottom: 'top',
    left: 'right',
  }[placement.split('-')[0]];
  const speechbubbleX = _.get(middlewareData, 'arrow.x', null);
  const speechbubbleY = _.get(middlewareData, 'arrow.y', null);

  const popover = (
    <FloatingNode id={nodeId}>
      {cloneElement(children, getReferenceProps({ ref, ...children.props }))}
      <FloatingPortal>
        {open && (
        <FloatingFocusManager
          context={context}
          order={focusOrder}
          modal
        >
          <div
            ref={floating}
            className={bindClasses('popover', {
              subdued,
              speechBubblePresent: speechBubble,
              submenu,
              noPadding,
              insideModal,
            },
            `padding-${padding}`)}
            style={{
              position: strategy,
              top: y ?? 0,
              left: x ?? 0,
            }}
            aria-labelledby={labelId}
            aria-describedby={descriptionId}
            {...getFloatingProps()}
            data-ppuc-id={`${dataId}.expanded`}
            onKeyDown={onKeyDown}
          >
            <ResizeAware
              wrapperStyles={{ position: 'relative', width: '100%', minWidth: '0' }}
              onResize={() => { update(); }}
            >
              {render({
                labelId,
                descriptionId,
                close: () => {
                  setOpen(false);
                },
              })}
            </ResizeAware>
            {speechBubble && (
            <div
              data-placement={staticSide}
              ref={arrowRef}
              className={bindClasses('popoverSpeechBubbleInner')}
              style={{
                left: speechbubbleX !== null ? `${speechbubbleX}px` : '',
                top: speechbubbleY !== null ? `${speechbubbleY}px` : '',
                right: '',
                bottom: '',
                [staticSide]: '-1px',
              }}
              data-ppuc-id={`${dataId}.speechBubble`}
            />
            )}
          </div>
        </FloatingFocusManager>
        )}
      </FloatingPortal>
    </FloatingNode>
  );
  if (parentId === null) {
    return (
      <FloatingTree>
        {popover}
      </FloatingTree>
    );
  }

  return popover;
};

PopoverWrapping.defaultProps = {
  position: 'bottom-start',
  disabled: false,
  speechBubble: false,
  subdued: false,
  hoverTrigger: false,
  hoverEnable: false,
  clickTrigger: false,
  submenu: false,
  noPadding: false,
  openProps: null,
  closedProps: null,
  fixedWidth: false,
  customMaxWidth: 0,
  minWidth: false,
  onKeyDown: () => {},
  dataId: 'Popover',
  onOpen: () => {},
  onClose: () => {},
};

PopoverWrapping.Section = PopoverSection;
PopoverWrapping.propEnums = propEnums;
export default PopoverWrapping;
